/**
 * What a phone number resolves to, rendered the way the pop will render it: one account; a chain
 * (one group per legal entity, the likely location first and marked); a recognised caller with no
 * account yet (a pending draft); or nobody. Rows are options in the lookup's listbox: the parent
 * owns the cursor and the keys, this component owns the copy.
 */
import { type CallerResolution, type EntityGroup, type PhoneMatch } from '@lsc/domain';
import { rowId } from '../../lib/lookup-plan';
import { Button } from '../ui/button';
import { StatusWord } from '../ui/status';

export interface ResolutionProps {
  r: CallerResolution;
  /** Open an account (click, or Enter from the lookup field). */
  onOpen: (accountId: string) => void;
  /** Index into `resolutionAccounts(r)`, the flattened display order. */
  cursor: number;
  onHover: (index: number) => void;
  /** For a recognised caller: link them (cleanup) or log a ticket on them now. Omitted inside quick capture. */
  draftActions?: { onLink: () => void; onStart: () => void } | undefined;
}

interface Group {
  entity: EntityGroup;
  rows: PhoneMatch[];
  chainLine: boolean;
}

/** The locations in display order: group by entity as `entities` orders them, the likely one first within its group. */
export function resolutionAccounts(r: CallerResolution): PhoneMatch[] {
  if (r.status === 'one') return [r.match];
  if (r.status !== 'many') return [];
  return groupsOf(r).flatMap((g) => g.rows);
}

/** The same grouping `groupEntities` in the domain does, with the rows attached and the likely one first. */
function groupsOf(r: Extract<CallerResolution, { status: 'many' }>): Group[] {
  const byKey = new Map<string, Group>();
  for (const m of r.matches) {
    const key = m.legalEntityId ?? `account:${m.accountId}`;
    const g = byKey.get(key) ?? { entity: { legalEntityId: m.legalEntityId, legalName: m.legalName, accounts: 0, open: 0 }, rows: [], chainLine: true };
    g.entity.accounts += 1;
    if (m.status !== 'closed') g.entity.open += 1;
    g.rows.push(m);
    g.chainLine = g.chainLine && (m.sources.includes('account_cs_phone') || m.sources.includes('entity_link'));
    byKey.set(key, g);
  }
  return [...byKey.values()]
    .sort((a, b) => b.entity.open - a.entity.open || b.entity.accounts - a.entity.accounts || (a.entity.legalName ?? '').localeCompare(b.entity.legalName ?? ''))
    .map((g) => ({ ...g, rows: [...g.rows].sort((a, b) => Number(b.accountId === r.suggestedAccountId) - Number(a.accountId === r.suggestedAccountId)) }));
}

function groupHeader(g: Group): string {
  const n = g.entity.accounts;
  return `${g.entity.legalName ?? 'No legal entity on file'}, ${n} ${n === 1 ? 'location' : 'locations'}, ${g.entity.open} live${g.chainLine ? ", this number is the chain's support line" : ''}`;
}

export function Resolution({ r, onOpen, cursor, onHover, draftActions }: ResolutionProps) {
  if (r.status === 'unparseable') {
    return <p className="mt-4 text-body text-ink-3">Not a phone number yet. Keep typing, or search by name.</p>;
  }
  if (r.status === 'none') {
    if (!r.draft) {
      return <p className="mt-4 text-body text-ink-3">No account or recognised caller for this number. Try the store or owner name.</p>;
    }
    const d = r.draft;
    return (
      <div className="mt-4 rounded-panel border border-line bg-surface px-4 py-3">
        <p className="text-body text-ink">
          Recognised: {d.contactName ?? 'unknown caller'}, {d.companyName ?? 'no store'} ({sourceName(d.source)}), not linked yet
        </p>
        {d.notes && <p className="mt-1 text-hint text-ink-3">{d.notes}</p>}
        {draftActions && (
          <div className="mt-3 flex gap-2">
            <Button variant="primary" onClick={draftActions.onStart}>
              Start a ticket
            </Button>
            <Button onClick={draftActions.onLink}>Link to an account</Button>
          </div>
        )}
      </div>
    );
  }

  const groups: Group[] = r.status === 'one' ? [] : groupsOf(r);
  const all = resolutionAccounts(r);
  const names = new Set(all.map((m) => m.contactName).filter((n): n is string => n !== null && n !== ''));
  const person = names.size === 1 && all.every((m) => m.contactName) ? [...names][0] : null;
  let index = 0;

  const row = (m: PhoneMatch) => {
    const i = index++;
    const selected = i === cursor;
    const likely = r.status === 'many' && r.suggestedAccountId === m.accountId;
    return (
      <li
        key={m.accountId}
        id={rowId(m.accountId)}
        role="option"
        aria-selected={selected}
        className={`flex cursor-pointer items-center gap-4 border-t border-line border-l-[3px] px-4 py-2 first:border-t-0 ${selected ? 'border-l-info bg-surface-2' : 'border-l-transparent hover:bg-surface-2/60'}`}
        onMouseEnter={() => {
          onHover(i);
        }}
        onClick={() => {
          onOpen(m.accountId);
        }}
      >
        <span className="min-w-0 flex-1 truncate text-body font-medium text-ink">
          {m.label}
          {m.city && <span className="font-normal text-ink-2">, {m.city}</span>}
          {likely && <span className="ml-2 text-hint font-normal text-info">likely</span>}
        </span>
        <span className="mono text-hint text-ink-2">{m.mid ?? ''}</span>
        <StatusWord status={m.status} className="w-28 text-hint" />
      </li>
    );
  };

  return (
    <div className="mt-4 overflow-hidden rounded-panel border border-line bg-surface">
      {person && <p className="border-b border-line px-4 py-2 text-body text-ink">{person} answers this line</p>}
      {r.status === 'one' ? (
        <ul role="listbox" aria-label="Accounts on this number">
          {row(r.match)}
        </ul>
      ) : (
        <>
          {groups.map((g) => (
            <section key={g.entity.legalEntityId ?? g.entity.legalName ?? 'none'}>
              <h2 className="border-b border-line bg-surface-2/40 px-4 py-2 text-hint text-ink-2">{groupHeader(g)}</h2>
              <ul role="listbox" aria-label={g.entity.legalName ?? 'Accounts on this number'}>
                {g.rows.map(row)}
              </ul>
            </section>
          ))}
          {r.truncated && (
            <p className="border-t border-line px-4 py-2 text-hint text-attention">
              {r.total} accounts on this number, showing {r.matches.length}
            </p>
          )}
        </>
      )}
    </div>
  );
}

function sourceName(source: string): string {
  switch (source) {
    case 'goto_import':
      return 'GoTo';
    case 'slack_import':
      return 'Slack';
    case 'dialpad':
      return 'Dialpad';
    default:
      return 'desk';
  }
}
